/**
 * Download service.
 */
import {throttle} from "lodash";

const electron = require('electron');
const electronApp = electron.app;
const electronDL = require('electron-dl');
const jetpack = require('fs-jetpack');

import Const from '../../../constants';
import JsHelper from '../../../helpers/jsHelper';

const log = (global as any).log;

class DownloadService {

    /**
     * Download a file.
     */
    static downloadAndInstall(programKey, url, installerType, installDir, cliArguments?: string) {
        log.debug(`> Kernel.DownloadService.downloadAndInstall() ${programKey}`);

        let extension;
        let filePath;

        if (installerType === Const.InstallerType.ZIP) {
            extension = '.zip';
        } else if (installerType === Const.InstallerType.ZIP7) {
            extension = '.7z';
        } else {
            extension = '.exe';
        }

        const window = global.mainWindow;
        const downloadDir = jetpack.cwd(electronApp.getPath('downloads'));
        const downloadSubDir = 'cenos-thirdparty';
        const filename = programKey + extension;

        try {

            // Create download directory. Note: Why 'dirAsync' does not reject on error?
            downloadDir.dir(downloadSubDir);

            // Delete previous download file if exists.
            if (downloadDir.cwd(downloadSubDir).exists(filename) !== false) {
                downloadDir.cwd(downloadSubDir).remove(filename);
            }

        } catch(err) {
            log.error('Could not start the download: ', err);
            return Promise.reject(err);
        }

        // Start download.
        return electronDL.download(window, url, {
            directory: downloadDir.path(downloadSubDir),
            filename: filename,

            onProgress: (progress) => {
                try {
                    window.webContents.send('cen-download-and-install-progress', {
                        url: url,
                        progress: progress.percent / 2,
                    });

                } catch (err) {

                    // Probably, user closed CENOS.
                    log.error('Could not send progress info: ', err);
                    electronApp.quit();
                }

            },
        })

        // Extract. TODO: Move to appropriate location?
        .then(item => {
            let promise;

            filePath = item.getSavePath();

            switch (installerType) {
                case Const.InstallerType.ZIP: {
                    promise = DownloadService.extractZip(url, filePath, installDir);
                    break;
                }

                case Const.InstallerType.ZIP7: {
                    promise = DownloadService.extract7Zip(url, filePath, installDir);
                    break;
                }

                case Const.InstallerType.EXE_7ZIP: {
                    promise = DownloadService.run7ZipSelfExtracting(filePath, installDir);
                    break;
                }

                case Const.InstallerType.EXE: {
                    promise = DownloadService.runExe(filePath, cliArguments);
                    break;
                }
            }

            return promise;
        })

        .then(() => {

            // Try to delete downloaded file.
            return JsHelper.wrapPromiseInTimeout(jetpack.removeAsync(filePath), 4000)

            .catch (err => {
                log.warn(`Could not delete downloaded file: ${filePath}: ${err}`);
            });

        })

        .then(() => {
            return installDir;
        });
    }

    /**
     * Extract zip file.
     */
    static extractZip(url, path, destination) {
        let decompressZip = require('decompress-zip');

        return new Promise<void>((resolve, reject) => {
            const unzipper = new decompressZip(path);

            const onProgressFn = (fileIndex, fileCount) => {
                let progress = (fileIndex + 1) / fileCount;

                try {
                    global.mainWindow.webContents.send('cen-download-and-install-progress', {
                        url: url,
                        progress: 0.5 + progress / 2,
                    });

                } catch (err) {

                    // Probably, user closed CENOS.
                    log.error('Could not send progress info: ', err);
                    electronApp.exit();
                }
            }

            const onProgressThrottleFn = throttle(onProgressFn.bind(this), 100, {leading: true, trailing: true});

            // On progress.
            unzipper.on('progress', onProgressThrottleFn);

            unzipper.on('extract', () => {
                resolve();
            });

            unzipper.on('error', err => {
                log.error('unzipper: ', err);
                reject(err);
            });

            // Start extracting.
            unzipper.extract({
                path: destination,
            });
        });
    }

    /**
     * Extract 7zip file.
     */
    static extract7Zip(url, path, destination) {
        const extractFull = require('node-7z').extractFull;

        return new Promise<void>((resolve, reject) => {
            const stream = extractFull(path, destination, {
                $bin: Const.path.zip7,
                $progress: true,
                recursive: true,
            });

            const onProgressFn = (progress) => {
                try {
                    global.mainWindow.webContents.send('cen-download-and-install-progress', {
                        url: url,
                        progress: 0.5 + progress.percent / 100 / 2,
                    });

                } catch (err) {

                    // Probably, user closed CENOS.
                    log.error('Could not send progress info: ', err);
                    electronApp.exit();
                }
            }

            const onProgressThrottleFn = throttle(onProgressFn.bind(this), 100, {leading: true, trailing: true});

            stream.on('progress', onProgressThrottleFn);

            stream.on('end', () => {
                resolve();
            })

            stream.on('error', err => {
                log.error('ERROR when extracting 7zip archive');
                log.error(err);
                reject(err);
            });
        });
    }

    /**
     * Run 7zip self-extracting executable.
     */
    static run7ZipSelfExtracting(path, destination) {
        let childProcess = require('child_process');

        return new Promise<void>((resolve, reject) => {
            let process = childProcess.execFile(path, [`-y`, `-o${destination}`]);

            process.on('exit', () => {
                resolve();
            });

            process.on('error', err => {
                reject(err);
            });
        });
    }

    /**
     * Run self-extracting executable.
     */
    static runExe(path, cliArguments) {
        let childProcess = require('child_process');

        return new Promise<void>((resolve, reject) => {
            let process = childProcess.execFile(path, [cliArguments]);

            process.on('exit', () => {
                resolve();
            });

            process.on('error', err => {
                reject(err);
            });
        });
    }
}

export default DownloadService;
